import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
  MinusCircle,
  History,
  Play,
} from "lucide-react";
import InternalVideoPreviewModal from "@/components/InternalVideoPreviewModal";
import { runLabel } from "@/lib/runNumber";
import { useAuth } from "@/lib/auth.jsx";

import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { api, formatApiError } from "@/lib/api";
import { formatRunNumber, runRef } from "@/lib/runNumber";
import FolderCountBadge, { useFolderSummary } from "@/components/FolderCountBadge";
import AddToFolderDropdown from "@/components/AddToFolderDropdown";
import BulkFolderMoveBar from "@/components/BulkFolderMoveBar";
import RecentActivityWidget from "@/components/RecentActivityWidget";
import ViewerActivityFeed from "@/components/ViewerActivityFeed";
import { Checkbox } from "@/components/ui/checkbox";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function formatDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export default function DemoRunsListPage() {
  const { hasPermission } = useAuth();
  const canPreview = hasPermission("workflow.run");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workflowFilter, setWorkflowFilter] = useState("all");
  const [emailQuery, setEmailQuery] = useState("");
  const [previewRun, setPreviewRun] = useState(null); // {run_id, run_number, workflow}

  const workflowsQuery = useQuery({
    queryKey: ["workflows", { status: "all", page_size: 200 }],
    queryFn: async () =>
      (await api.get("/workflows", { params: { status: "all", page_size: 200 } })).data,
  });

  const runs = useQuery({
    queryKey: ["demo-runs", statusFilter, workflowFilter, emailQuery],
    queryFn: async () => {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (workflowFilter !== "all") params.workflow_id = workflowFilter;
      if (emailQuery.trim()) params.q = emailQuery.trim();
      return (await api.get("/demo-runs", { params })).data;
    },
    refetchInterval: 5000,
  });

  const workflowOptions = useMemo(
    () => workflowsQuery.data?.items ?? [],
    [workflowsQuery.data],
  );

  const runIds = useMemo(
    () => (runs.data?.items ?? []).map((r) => r.run_id),
    [runs.data],
  );
  const folderSummaryQuery = useFolderSummary(runIds);
  const folderSummary = folderSummaryQuery.data || {};

  // Sec/UX (2026-05-26): multi-select for bulk folder ops.
  // Reset when the underlying list changes filters so we never act on
  // stale rows.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, workflowFilter, emailQuery]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const allSelected = runIds.length > 0 && runIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(runIds);
    });
  };
  const allPageSelected =
    runIds.length > 0 && runIds.every((id) => selectedIds.has(id));
  const somePageSelected = !allPageSelected && runIds.some((id) => selectedIds.has(id));

  return (
    <div className="space-y-6" data-testid="runs-list-page">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <History size={18} className="text-indigo-600" />
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Demo Runs
          </h1>
        </div>
        <p className="max-w-2xl text-sm text-slate-600">
          Every demo run started from the workflow library shows up here.
          Live status auto-refreshes every 5 seconds.
        </p>
      </header>

      <RecentActivityWidget
        limit={15}
        title="Recent activity"
        emptyHint="No audit events yet. Run a workflow to see activity here."
        compact={false}
      />

      <ViewerActivityFeed limit={8} sinceHours={72} dataTestidPrefix="runs-viewer-activity" />

      <section
        className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3"
        data-testid="runs-filters"
      >
        <Filter label="Status">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`filter-status-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Filter>

        <Filter label="Workflow">
          <Select value={workflowFilter} onValueChange={setWorkflowFilter}>
            <SelectTrigger data-testid="filter-workflow">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-workflow-all">All workflows</SelectItem>
              {workflowOptions.map((w) => (
                <SelectItem
                  key={w.workflow_id}
                  value={w.workflow_id}
                  data-testid={`filter-workflow-${w.slug}`}
                >
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Filter>

        <Filter label="Search (email or run #)">
          <Input
            placeholder="0042 · #42 · mike@eyvo.com"
            value={emailQuery}
            onChange={(e) => setEmailQuery(e.target.value)}
            data-testid="filter-triggered-by"
          />
        </Filter>
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {runs.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : runs.isError ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            data-testid="runs-error"
          >
            {formatApiError(runs.error)}
          </div>
        ) : (runs.data?.items ?? []).length === 0 ? (
          <div
            className="px-6 py-12 text-center text-sm text-slate-500"
            data-testid="runs-empty"
          >
            No runs yet. Start one from a workflow&apos;s detail page.
          </div>
        ) : (
          <Table data-testid="runs-table">
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-9 px-3 text-xs uppercase tracking-wider text-slate-500">
                  <Checkbox
                    checked={allPageSelected ? true : somePageSelected ? "indeterminate" : false}
                    onCheckedChange={togglePageSelection}
                    aria-label="Select all rows on this page"
                    data-testid="runs-select-all"
                  />
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Run #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Workflow</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">v</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Video</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cost</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Started</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Duration</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Triggered by</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">📁</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.data.items.map((r) => (
                <TableRow key={r.run_id} data-testid={`run-row-${r.run_id}`}>
                  <TableCell className="px-3">
                    <Checkbox
                      checked={selectedIds.has(r.run_id)}
                      onCheckedChange={() => toggleSelected(r.run_id)}
                      aria-label={`Select run ${formatRunNumber(r.run_number)}`}
                      data-testid={`runs-select-${r.run_id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-indigo-700" data-testid={`runs-row-number-${r.run_id}`}>{runRef(r.run_number)}</TableCell>
                  <TableCell className="font-medium text-slate-800">{r.workflow_name_snapshot}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">v{r.workflow_version}</TableCell>
                  <TableCell><RunStatusPill status={r.status} /></TableCell>
                  <TableCell>
                    <VideoStatusPill
                      status={r.video_status}
                      runId={r.run_id}
                      runNumber={r.run_number}
                      workflowName={r.workflow_name_snapshot}
                      onPreview={canPreview ? setPreviewRun : null}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <RunCostCell amount={r.cost_total_usd} runId={r.run_id} />
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDate(r.started_at)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDuration(r.duration_ms)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{r.triggered_by}</TableCell>
                  <TableCell>
                    <AddToFolderDropdown runId={r.run_id}>
                      <button
                        type="button"
                        className="cursor-pointer transition-opacity hover:opacity-80"
                        aria-label="Manage folders for this run"
                        data-testid={`runs-folder-trigger-${r.run_id}`}
                      >
                        <FolderCountBadge summary={folderSummary[r.run_id]} runId={r.run_id} />
                      </button>
                    </AddToFolderDropdown>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/runs/${r.run_id}`}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                      data-testid={`run-open-${r.run_id}`}
                    >
                      Open →
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
      <BulkFolderMoveBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds(new Set())}
      />

      <InternalVideoPreviewModal
        open={!!previewRun}
        onOpenChange={(o) => !o && setPreviewRun(null)}
        runId={previewRun?.run_id}
        runNumberLabel={previewRun ? runLabel(previewRun.run_number) : ""}
        workflowName={previewRun?.workflow_name}
      />
    </div>
  );
}

function RunCostCell({ amount, runId }) {
  if (amount == null) {
    return (
      <span
        className="font-mono text-xs text-slate-300"
        data-testid={`runs-cost-${runId}`}
        data-has-cost="false"
      >
        —
      </span>
    );
  }
  return (
    <span
      className="font-mono text-xs text-slate-900"
      data-testid={`runs-cost-${runId}`}
      data-has-cost="true"
    >
      {`$${Number(amount).toFixed(4)}`}
    </span>
  );
}

function VideoStatusPill({ status, runId, runNumber, workflowName, onPreview }) {
  // R3 UI close-out — pill matches the design spec.  When status="ready"
  // and the caller has presenter+ permission, clicking the pill opens
  // the internal preview modal directly.
  const map = {
    ready: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      label: "▶ Ready",
      pulse: false,
      tooltip: "Open internal preview",
    },
    stitching: {
      cls: "bg-indigo-50 text-indigo-700 border-indigo-200 animate-pulse",
      label: "Stitching…",
      pulse: true,
      tooltip: "Final MP4 is being produced",
    },
    recording: {
      cls: "bg-indigo-50 text-indigo-700 border-indigo-200 animate-pulse",
      label: "Recording…",
      pulse: true,
      tooltip: "Playwright is driving the browser",
    },
    failed: {
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      label: "Failed",
      pulse: false,
      tooltip: "Generation failed for this run",
    },
    none: {
      cls: "bg-slate-50 text-slate-500 border-slate-200",
      label: "—",
      pulse: false,
      tooltip: "No video for this run",
    },
  };
  const cfg = map[status] || map.none;
  const clickable = status === "ready" && typeof onPreview === "function";
  const base = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`;
  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => onPreview({ run_id: runId, run_number: runNumber, workflow_name: workflowName })}
        className={`${base} cursor-pointer hover:brightness-95`}
        title={cfg.tooltip}
        data-testid={`runs-video-status-${runId}`}
        data-video-status={status}
      >
        {cfg.label}
      </button>
    );
  }
  return (
    <span
      className={base}
      title={cfg.tooltip}
      data-testid={`runs-video-status-${runId}`}
      data-video-status={status || "none"}
    >
      {cfg.label}
    </span>
  );
}

function Filter({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      {children}
    </div>
  );
}

export function RunStatusPill({ status }) {
  const map = {
    pending: { Icon: Clock, cls: "bg-slate-100 text-slate-700", label: "Pending" },
    running: { Icon: Loader2, cls: "bg-indigo-100 text-indigo-800 animate-pulse", label: "Running" },
    completed: { Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700", label: "Completed" },
    failed: { Icon: XCircle, cls: "bg-rose-50 text-rose-700", label: "Failed" },
    cancelled: { Icon: MinusCircle, cls: "bg-amber-50 text-amber-800", label: "Cancelled" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      data-testid={`run-status-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${m.cls}`}
    >
      <m.Icon size={11} className={status === "running" ? "animate-spin" : ""} />
      {m.label}
    </span>
  );
}
